import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Clock, ChevronRight, Wrench } from "lucide-react";
import api from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader, EmptyState } from "@/components/common";
import { INSTALL_STATUS, statusMeta } from "@/lib/labels";

const TABS = [
  { key: "todo", label: "À traiter", match: ["affectee"] },
  { key: "accepted", label: "Acceptées", match: ["acceptee"] },
  { key: "progress", label: "En cours", match: ["en_route", "arrive", "commencee"] },
  { key: "refused", label: "Refusées", match: ["refusee"] },
  { key: "done", label: "Terminées", match: ["terminee", "validee", "cloturee"] },
];

export default function ProDashboard() {
  const [missions, setMissions] = useState(null);
  const [tab, setTab] = useState("todo");
  useEffect(() => { api.get("/pro/missions").then((r) => setMissions(r.data)); }, []);
  if (!missions) return <Loader label="Chargement des missions..." />;

  const count = (t) => missions.filter((m) => t.match.includes(m.status)).length;
  const current = TABS.find((t) => t.key === tab);
  const list = missions.filter((m) => current.match.includes(m.status));

  return (
    <div>
      <h1 className="font-display text-3xl font-black uppercase mb-1">Mes interventions</h1>
      <p className="text-slate-400 text-sm mb-4">{count(TABS[0]) + count(TABS[1]) + count(TABS[2])} mission(s) actives</p>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`pro-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${tab === t.key ? "bg-[#E08B00] text-[#0F172A]" : "bg-slate-800 text-slate-400"}`}>
            {t.label}
            <span className={`text-[10px] rounded-full px-1.5 ${tab === t.key ? "bg-[#0F172A]/20" : "bg-slate-700"}`}>{count(t)}</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <EmptyState icon={Wrench} title="Aucune mission" subtitle="Aucune intervention dans cette catégorie." />
      ) : (
        <div className="space-y-3">{list.map((m) => <MissionCard key={m.id} m={m} />)}</div>
      )}
    </div>
  );
}

function MissionCard({ m }) {
  const meta = statusMeta(INSTALL_STATUS, m.status);
  return (
    <Link to={`/pro/mission/${m.id}`} data-testid={`mission-card-${m.id}`}
      className="block bg-[#1F2937] border-2 border-slate-700 rounded-xl p-4 hover:border-[#E08B00]/50 transition">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-num font-bold text-white">{m.mission_number}</span>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
          {m.urgency === "urgent" && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">⚡ URGENT</span>}
        </div>
        <ChevronRight className="w-5 h-5 text-slate-500" />
      </div>
      <p className="text-sm font-semibold text-slate-200 mt-2">{m.product_name}</p>
      <p className="text-xs text-slate-400">{m.customer_name}</p>
      {m.status === "refusee" && m.refusal_reason && <p className="text-xs text-red-400 mt-1">Motif refus : {m.refusal_reason}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400 mt-2">
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {m.zone}</span>
        {m.scheduled_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(m.scheduled_date)}</span>}
        {m.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {m.scheduled_time}</span>}
      </div>
      {m.pro_amount > 0 && <p className="font-num text-[#E08B00] font-bold mt-2">{formatFCFA(m.pro_amount)}</p>}
    </Link>
  );
}
